import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from 'components/mui/common/Select';
import { ActionModal } from 'components/mui/common/ActionModal';
import { MenuItem } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (type: AgendaViewType) => void;
}

export type AgendaViewType = 'AgendaView' | 'Notification' | 'NotificationWithAction';

const AddAgendaViewModalBase = ({ open, onClose, onAdd }: Props) => {
  const { t } = useTranslation();

  const [type, setType] = useState<AgendaViewType | undefined>();

  const options = [
    { value: 'AgendaView', label: t('core.settings.dashboard.agendaView') },
    { value: 'Notification', label: t('core.settings.dashboard.notification') },
    { value: 'NotificationWithAction', label: t('core.settings.dashboard.notificationWithAction') },
  ];

  const handleSelectType = useCallback((e: SelectChangeEvent<unknown>) => setType(e.target.value as AgendaViewType), []);

  const handleSubmit = useCallback(() => {
    if (!type) return;
    onAdd(type);
    onClose();
  }, [onAdd, onClose, type]);

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={t('core.settings.dashboard.agendaViewSettings')}
      submitText={t('common.confirm')}
      onCancel={onClose}
      onSubmit={handleSubmit}
      isOnSubmitDisabled={!type}>
      <Select label={t('common.type')} value={type} onChange={handleSelectType} displayEmpty fullWidth>
        {options.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
    </ActionModal>
  );
};

export const AddAgendaViewModal = memo(AddAgendaViewModalBase) as typeof AddAgendaViewModalBase;
